import { useState } from 'react'

import { useAction } from '../components/toast'
import { Badge, Card, Loaded, Modal, Notice, Page, Table } from '../components/ui'
import { get, put } from '../lib/api'
import { money, titleCase, tokens } from '../lib/format'
import { useResource } from '../lib/useResource'

type Model = {
  id: string
  label: string
  provider: string
  context_window: number
  input_price_per_mtok: number
  output_price_per_mtok: number
  phi_permitted: boolean
  price_verified: boolean
  recommended_for: string[]
}

export function AiModels() {
  const models = useResource((signal) => get<Model[]>('/v1/admin/ai/models', undefined, signal))
  const [editing, setEditing] = useState<Model | null>(null)
  const [provider, setProvider] = useState('')

  const rows = (models.data ?? []).filter((m) => !provider || m.provider === provider)
  const providers = [...new Set((models.data ?? []).map((m) => m.provider))].sort()
  const unverified = (models.data ?? []).filter((m) => !m.price_verified)

  return (
    <Page
      title="Model catalog"
      subtitle="Every model the gateway can route to, across Claude, OpenAI, Gemini, OpenRouter, Alibaba and custom endpoints."
      actions={
        <select
          className="select"
          style={{ width: 190 }}
          value={provider}
          onChange={(event) => setProvider(event.target.value)}
          aria-label="Filter by provider"
        >
          <option value="">All providers</option>
          {providers.map((p) => (
            <option key={p} value={p}>
              {titleCase(p)}
            </option>
          ))}
        </select>
      }
    >
      {unverified.length > 0 && (
        <Notice tone="warn">
          <strong>{unverified.length} model{unverified.length === 1 ? '' : 's'}</strong> carry prices
          transcribed from memory rather than confirmed against the provider's published rate. Cost
          figures involving them are estimates. Open a row, check the price against the provider's
          pricing page, and mark it verified — that is the only thing that clears this banner.
        </Notice>
      )}

      <Notice>
        <strong>PHI-permitted is an assertion about paperwork, not about the model.</strong> Turning
        it on says a signed agreement exists that allows protected health information to reach that
        vendor. The API refuses the change without a written reason naming the agreement, and the
        redaction layer still runs regardless.
      </Notice>

      <Card flush>
        <Loaded resource={models}>
          {() => (
            <Table
              rows={rows}
              rowKey={(row) => row.id}
              onRowClick={(row) => setEditing(row)}
              empty="No models match that filter."
              columns={[
                {
                  key: 'model',
                  header: 'Model',
                  strong: true,
                  render: (row) => (
                    <span>
                      <span style={{ display: 'block' }}>{row.label}</span>
                      <span className="mono dim">{row.id}</span>
                    </span>
                  ),
                },
                { key: 'provider', header: 'Provider', render: (row) => <Badge>{titleCase(row.provider)}</Badge> },
                {
                  key: 'ctx',
                  header: 'Context',
                  render: (row) => <span className="num">{tokens(row.context_window)}</span>,
                },
                {
                  key: 'price',
                  header: 'Price / Mtok',
                  render: (row) => (
                    <span className="num">
                      {money(row.input_price_per_mtok)} in · {money(row.output_price_per_mtok)} out
                    </span>
                  ),
                },
                {
                  key: 'verified',
                  header: 'Price',
                  render: (row) =>
                    row.price_verified ? (
                      <Badge tone="green">Verified</Badge>
                    ) : (
                      <Badge tone="orange">Unverified</Badge>
                    ),
                },
                {
                  key: 'phi',
                  header: 'PHI',
                  render: (row) =>
                    row.phi_permitted ? (
                      <Badge tone="alert">Permitted</Badge>
                    ) : (
                      <Badge>Redacted only</Badge>
                    ),
                },
                {
                  key: 'good',
                  header: 'Recommended for',
                  render: (row) => (
                    <span className="row row--wrap" style={{ gap: 4 }}>
                      {row.recommended_for.length === 0 ? (
                        <span className="dim">—</span>
                      ) : (
                        row.recommended_for.map((task) => <Badge key={task}>{task}</Badge>)
                      )}
                    </span>
                  ),
                },
              ]}
            />
          )}
        </Loaded>
      </Card>

      {editing && (
        <ModelEditor model={editing} onClose={() => setEditing(null)} onSaved={models.reload} />
      )}
    </Page>
  )
}

function ModelEditor({
  model,
  onClose,
  onSaved,
}: {
  model: Model
  onClose: () => void
  onSaved: () => void
}) {
  const run = useAction()
  const [inputPrice, setInputPrice] = useState(String(model.input_price_per_mtok))
  const [outputPrice, setOutputPrice] = useState(String(model.output_price_per_mtok))
  const [phi, setPhi] = useState(model.phi_permitted)
  const [verified, setVerified] = useState(model.price_verified)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  // The API enforces this too; mirroring it here means the operator sees why the
  // button is disabled instead of getting a 400 after typing.
  const reasonRequired = phi && !model.phi_permitted
  const canSave = !reasonRequired || reason.trim().length >= 10

  const save = async () => {
    setBusy(true)
    await run(
      () =>
        put(`/v1/admin/ai/models/${model.id}`, {
          input_price_per_mtok: Number(inputPrice),
          output_price_per_mtok: Number(outputPrice),
          phi_permitted: phi,
          price_verified: verified,
          reason: reason.trim(),
        }),
      `${model.label} updated.`,
      () => {
        onSaved()
        onClose()
      },
    )
    setBusy(false)
  }

  return (
    <Modal title={model.label} onClose={onClose}>
      <div className="stack">
        <div className="row row--wrap">
          <Badge>{titleCase(model.provider)}</Badge>
          <Badge>{tokens(model.context_window)} context</Badge>
          {model.recommended_for.map((task) => (
            <Badge key={task} tone="lime">
              {task}
            </Badge>
          ))}
        </div>

        <div className="grid grid--2" style={{ gap: 12 }}>
          <div className="field">
            <label className="field__label" htmlFor="in-price">
              Input price · USD per million tokens
            </label>
            <input
              id="in-price"
              className="input"
              type="number"
              step="0.01"
              min="0"
              value={inputPrice}
              onChange={(event) => setInputPrice(event.target.value)}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="out-price">
              Output price · USD per million tokens
            </label>
            <input
              id="out-price"
              className="input"
              type="number"
              step="0.01"
              min="0"
              value={outputPrice}
              onChange={(event) => setOutputPrice(event.target.value)}
            />
          </div>
        </div>

        <label className="row" style={{ gap: 8, fontSize: 12.5 }}>
          <input
            type="checkbox"
            checked={verified}
            onChange={(event) => setVerified(event.target.checked)}
          />
          I checked this price against the provider's published rate today
        </label>

        <label className="row" style={{ gap: 8, fontSize: 12.5 }}>
          <input type="checkbox" checked={phi} onChange={(event) => setPhi(event.target.checked)} />
          PHI permitted — an executed agreement covers this vendor
        </label>

        {reasonRequired && (
          <>
            <Notice tone="phi">
              You are asserting that a signed agreement permits protected health information to
              reach {titleCase(model.provider)}. Name it below; the text goes into the audit log
              and is what a compliance review reads.
            </Notice>
            <div className="field">
              <label className="field__label" htmlFor="phi-reason">
                Which agreement, and when it was signed (minimum 10 characters)
              </label>
              <textarea
                id="phi-reason"
                className="textarea"
                style={{ minHeight: 74 }}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="e.g. BAA executed 2026-03-14, counsel ref LEG-2291"
              />
            </div>
          </>
        )}

        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={!canSave || busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
